import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { History, RefreshCw, Download, ArrowUpRight, ArrowDownRight, X } from "lucide-react";

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const ALL = "__all__";

export default function InventoryLog({ refreshKey, bomItems = [] }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [actorOpts, setActorOpts] = useState([]);
  const [item, setItem] = useState(ALL);
  const [actor, setActor] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    const qs = new URLSearchParams({ limit: "300" });
    if (item !== ALL) qs.set("item_id", item);
    if (actor !== ALL) qs.set("actor", actor);
    if (from) qs.set("date_from", from);
    if (to) qs.set("date_to", to);
    try {
      const data = await api.get(`/inventory/logs?${qs.toString()}`);
      setLogs(data);
      setActorOpts((prev) => Array.from(new Set([...prev, ...data.map((d) => d.actor)])).sort());
    } catch (e) {
      logger.error("Failed to load inventory logs:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [item, actor, from, to]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const hasFilters = item !== ALL || actor !== ALL || from || to;
  const clearFilters = () => { setItem(ALL); setActor(ALL); setFrom(""); setTo(""); };

  const itemOptions = useMemo(
    () => [...bomItems].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [bomItems],
  );

  const exportCsv = () => {
    const head = ["When", "SKU", "Item", "Change", "Balance", "Reason", "User"];
    const rows = logs.map((l) => [l.at, l.sku, l.name, l.delta, l.new_stock, l.reason, l.actor]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [head, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="inventory-log-panel">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-display text-base font-semibold text-slate-900">Inventory Activity</h3>
            <p className="text-xs text-slate-400">Who changed stock, when and why</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={logs.length === 0} data-testid="inventory-log-export">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={load} data-testid="inventory-log-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-end gap-3 bg-slate-50/60">
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-slate-400 block">Item</label>
          <Select value={item} onValueChange={setItem}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="inventory-filter-item"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Items</SelectItem>
              {itemOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.sku})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-slate-400 block">User</label>
          <Select value={actor} onValueChange={setActor}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="inventory-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Users</SelectItem>
              {actorOpts.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-slate-400 block">From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="inventory-filter-from" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wide text-slate-400 block">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="inventory-filter-to" />
        </div>
        {hasFilters && (
          <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid="inventory-filter-clear">
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
        <span className="ml-auto text-[11px] text-slate-400 self-center" data-testid="inventory-log-count">{logs.length} movement{logs.length === 1 ? "" : "s"}</span>
      </div>

      <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3 font-semibold">When</th>
              <th className="px-4 py-3 font-semibold">Item</th>
              <th className="px-4 py-3 font-semibold text-right">Change</th>
              <th className="px-4 py-3 font-semibold text-right">Balance</th>
              <th className="px-4 py-3 font-semibold">Reason</th>
              <th className="px-4 py-3 font-semibold">User</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => {
              const up = l.delta >= 0;
              return (
                <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="inventory-log-row">
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap text-xs">{fmtWhen(l.at)}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-slate-800">{l.name}</span>
                    <span className="text-[11px] text-slate-400 font-mono-data ml-1">{l.sku}</span>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono-data font-semibold ${up ? "text-emerald-600" : "text-red-600"}`}>
                    <span className="inline-flex items-center gap-0.5 justify-end">
                      {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                      {up ? "+" : ""}{l.delta}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-data text-slate-600">{l.new_stock}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{l.reason}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-[11px] font-mono-data px-2 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`inventory-log-actor-${l.id}`}>{l.actor}</span>
                  </td>
                </tr>
              );
            })}
            {!loading && !error && logs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400 italic" data-testid="inventory-log-empty">{hasFilters ? "No movements match these filters." : "No stock movements recorded yet."}</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-red-500" data-testid="inventory-log-error">Couldn't load the inventory activity. Please try Refresh.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
